import {
    Component,
    Renderer2,
    NgZone,
    AfterViewInit,
    OnInit,
    OnDestroy,
    Input,
    Output,
    EventEmitter,
    SimpleChanges,
    OnChanges,
} from '@angular/core';
import { State, process } from '@progress/kendo-data-query';
import { Subscription, fromEvent } from 'rxjs';
import { tap } from 'rxjs/operators';
import { RowClassArgs } from '@progress/kendo-angular-grid';

const tableRow = (node) => node.tagName.toLowerCase() === 'tr';

const closest = (node, predicate) => {
    while (node && !predicate(node)) {
        node = node.parentNode;
    }

    return node;
};

export interface IColumnNames {
    columnName: string;
    columnId: string;
    width: number;
}

@Component({
    selector: 'dragndrop-table',
    templateUrl: './dragndrop-table.component.html',
    styleUrls: ['./dragndrop-table.component.scss'],
})
export class DragndropTableComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    constructor(private renderer: Renderer2, private zone: NgZone) {}

    @Input() dataObjectArray: Array<any>;
    @Input() isDragable?: boolean = false;
    @Input() resetData?: boolean = false;
    @Input() columnList: IColumnNames[] = [];
    @Output() emitModifidedObjectArray: EventEmitter<Array<any>> = new EventEmitter<Array<any>>();
    dataObjectArrayInternalUse: Array<any>;
    state: State;
    gridData: any;
    pageable: boolean;
    private currentSubscription: Subscription;

    dataObjectArrayDragZoneCopy: Array<any>;
    dragableNodes: HTMLCollectionOf<Element>;
    selectedNodePos = 0;
    isNodePosSet = false;
    isNodeRemoved = false;

    ngOnInit() {
        this.dataObjectArrayDragZoneCopy = this.dataObjectArray.map((obj) => ({
            ...obj,
        }));
        this.setGridData();
    }

    setGridData() {
        let take = 100;
        this.state = {
            skip: 0,
            take: take,
        };

        this.pageable = this.dataObjectArray.length > take ? true : false;
        this.dataObjectArrayInternalUse = this.updateTheOrder(this.dataObjectArray);
        this.gridData = process(this.dataObjectArrayInternalUse, this.state);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.resetData) {
            let change: boolean = changes.resetData.currentValue;
            if (change) {
                this.setGridData();
            }
        }
    }

    sortArray(array: Array<any>) {
        return array.map((obj) => ({ ...obj })).sort((a, b) => a.orderIndex - b.orderIndex);
    }

    updateTheOrder(array: Array<any>) {
        let indexColName = 'orderIndex';
        array.map((obj, index) => {
            obj[indexColName] = index;
        });

        return this.sortArray(array);
    }

    public ngAfterViewInit(): void {
        this.currentSubscription = this.handleDragAndDrop();
    }

    public ngOnDestroy(): void {
        this.currentSubscription.unsubscribe();
    }

    public rowCallback(context: RowClassArgs) {
        return {
            dragging: context.dataItem.dragging,
        };
    }

    private handleDragAndDrop(): Subscription {
        const sub = new Subscription(() => {});
        if (this.isDragable) {
            let draggedItemIndex;

            const tableRows = Array.from(document.querySelectorAll('.k-grid tr'));
            tableRows.forEach((item) => {
                this.renderer.setAttribute(item, 'draggable', 'true');
                const dragStart = fromEvent<DragEvent>(item, 'dragstart');
                const dragOver = fromEvent(item, 'dragover');
                const dragEnd = fromEvent(item, 'dragend');
                const mouseOver = fromEvent(item, 'mouseover');
                const mouseDown = fromEvent(item, 'mousedown');
                const mouseUp = fromEvent(item, 'mouseup');

                sub.add(
                    dragStart
                        .pipe(
                            tap(({ dataTransfer }) => {
                                try {
                                } catch (err) {
                                    // IE doesn't support setDragImage
                                }
                                try {
                                    // Firefox won't drag without setting data
                                    dataTransfer.setData('application/json', '{}');
                                } catch (err) {
                                    // IE doesn't support MIME types in setData
                                }
                            })
                        )
                        .subscribe((e: Event) => {
                            const row: HTMLTableRowElement = <HTMLTableRowElement>e.target;
                            item.setAttribute(
                                'style',
                                'cursor: -webkit-grabbing; cursor: grabbing; background-color: #223540;'
                            );
                            draggedItemIndex = row.rowIndex;
                            const dataItem = this.gridData.data[draggedItemIndex];
                            dataItem.dragging = true;
                        })
                );

                sub.add(
                    dragOver.subscribe((e: Event) => {
                        e.preventDefault();
                        item.setAttribute(
                            'style',
                            'background-color: "";  cursor: grabbing; cursor: -webkit-grabbing;'
                        );
                        const dataItem = this.gridData.data.splice(draggedItemIndex, 1)[0];
                        const dropIndex = closest(e.target, tableRow).rowIndex;
                        const dropItem = this.gridData.data[dropIndex];
                        draggedItemIndex = dropIndex;
                        this.zone.run(() => {
                            this.gridData.data.splice(dropIndex, 0, dataItem);
                        });
                    })
                );

                sub.add(
                    dragEnd.subscribe((e: Event) => {
                        e.preventDefault();
                        item.setAttribute(
                            'style',
                            'background-color: ""; cursor: -webkit-grab; cursor: grab;'
                        );
                        const dataItem = this.gridData.data[draggedItemIndex];
                        dataItem.dragging = false;
                        this.dataObjectArrayInternalUse = this.updateTheOrder(this.gridData.data);
                        this.emitModifidedObjectArray.emit(this.dataObjectArrayInternalUse);
                    })
                );

                sub.add(
                    mouseOver.subscribe((e: Event) => {
                        item.setAttribute('style', 'cursor: -webkit-grab; cursor: grab;');
                    })
                );

                sub.add(
                    mouseDown.subscribe((e: Event) => {
                        item.setAttribute(
                            'style',
                            'cursor: -webkit-grabbing; cursor: grabbing; background-color: #223540; color: #FFFFFF'
                        );
                    })
                );

                sub.add(
                    mouseUp.subscribe((e: Event) => {
                        item.setAttribute(
                            'style',
                            'cursor: -webkit-grab; cursor: grab; background-color: "";'
                        );
                    })
                );
            });
        }
        return sub;
    }

    getHeaderStyles() {
        return {
            'text-transform': 'uppercase',
            color: '#223540',
            'font-weight': '600',
        };
    }
}
